import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  UserPlus, 
  TrendingUp, 
  AlertTriangle, 
  Clock, 
  Shield, 
  ArrowLeft,
  RefreshCw,
  Eye,
  Calendar,
  Activity,
  Zap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getAllUsers, 
  getAllInvitations, 
  formatDateTime,
  formatDate,
  getRoleDisplayName,
  calculateSessionDuration
} from '../lib/userService';
import Header from './Header';

const UserActivityDashboard = () => {
  const { isAdmin } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    recentRegistrations: [],
    roleChanges: [],
    loginActivity: [],
    securityAlerts: {
      inactiveUsers: [],
      multipleLogins: [],
      passwordResets: []
    },
    stats: {
      totalUsers: 0,
      activeUsers: 0,
      newUsersThisWeek: 0,
      totalSessions: 0,
      userTrends: { direction: 'up', percentage: 0 }
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    if (isAdmin()) {
      fetchDashboardData();
      
      // Auto-refresh every 5 minutes
      const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [users, invitations] = await Promise.all([
        getAllUsers(),
        getAllInvitations()
      ]);
      
      // Get actual session data from the session service
      const { getAllActiveSessions } = await import('../lib/sessionService');
      const sessions = await getAllActiveSessions();

      // Calculate stats
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      // Recent registrations (last 7 days)
      const recentRegistrations = users
        .filter(user => {
          const createdAt = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
          return createdAt >= oneWeekAgo;
        })
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        })
        .slice(0, 10);

      // Active users (logged in within last 30 days)
      const activeUsers = users.filter(user => {
        if (!user.lastLogin) return false;
        const lastLogin = user.lastLogin.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin);
        return lastLogin >= oneMonthAgo;
      });

      // New users this week
      const newUsersThisWeek = recentRegistrations.length;

      // Previous week for trend calculation
      const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      const previousWeekUsers = users.filter(user => {
        const createdAt = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
        return createdAt >= twoWeeksAgo && createdAt < oneWeekAgo;
      }).length;

      // Calculate trend
      const userTrends = {
        direction: newUsersThisWeek >= previousWeekUsers ? 'up' : 'down',
        percentage: previousWeekUsers > 0 
          ? Math.abs(((newUsersThisWeek - previousWeekUsers) / previousWeekUsers) * 100)
          : 0
      };

      // Most active users (by recent login)
      const loginActivity = users
        .filter(user => user.lastLogin)
        .sort((a, b) => {
          const dateA = a.lastLogin?.toDate ? a.lastLogin.toDate() : new Date(a.lastLogin);
          const dateB = b.lastLogin?.toDate ? b.lastLogin.toDate() : new Date(b.lastLogin);
          return dateB - dateA;
        })
        .slice(0, 10);

      // Security alerts
      const inactiveUsers = users.filter(user => {
        if (!user.lastLogin) return true;
        const lastLogin = user.lastLogin.toDate ? user.lastLogin.toDate() : new Date(user.lastLogin);
        const daysSinceLogin = (now - lastLogin) / (1000 * 60 * 60 * 24);
        return daysSinceLogin > 30;
      });

      // Mock data for demonstration - in real app, you'd track these
      const multipleLogins = users.filter(user => user.email && user.email.includes('admin')).slice(0, 3);
      const passwordResets = users.filter(user => user.email && user.email.includes('test')).slice(0, 2);

      // Mock role changes - in real app, you'd fetch from audit logs
      const roleChanges = [
        {
          id: '1',
          userId: users[0]?.id,
          userName: users[0]?.displayName || users[0]?.email,
          oldRole: 'solo_lectura',
          newRole: 'shopkeeper',
          changedBy: 'admin@stb.com',
          changedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)
        },
        {
          id: '2',
          userId: users[1]?.id,
          userName: users[1]?.displayName || users[1]?.email,
          oldRole: 'shopkeeper',
          newRole: 'admin',
          changedBy: 'admin@stb.com',
          changedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)
        }
      ].filter(change => change.userName);

      setDashboardData({
        recentRegistrations,
        roleChanges,
        loginActivity,
        securityAlerts: {
          inactiveUsers,
          multipleLogins,
          passwordResets
        },
        stats: {
          totalUsers: users.length,
          activeUsers: activeUsers.length,
          newUsersThisWeek,
          totalSessions: sessions.length,
          userTrends
        }
      });
      
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-6xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            No tienes permisos para acceder a esta sección
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to="/users"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver a Gestión de Usuarios</span>
          </Link>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-6 w-6 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-900">Panel de Actividad de Usuarios</h2>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  Última actualización: {formatDateTime(lastRefresh)}
                </span>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="inline-flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Actualizar</span>
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          {loading ? (
            <div className="p-6 text-center py-8 text-gray-500">
              Cargando datos del dashboard...
            </div>
          ) : (
            <div className="p-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-blue-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total Usuarios</p>
                      <p className="text-2xl font-bold text-blue-900">{dashboardData.stats.totalUsers}</p>
                    </div>
                    <Users className="h-8 w-8 text-blue-600" />
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Usuarios Activos</p>
                      <p className="text-2xl font-bold text-green-900">{dashboardData.stats.activeUsers}</p>
                    </div>
                    <Zap className="h-8 w-8 text-green-600" />
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">Nuevos Esta Semana</p>
                      <p className="text-2xl font-bold text-purple-900">{dashboardData.stats.newUsersThisWeek}</p>
                      <div className="flex items-center space-x-1 mt-1">
                        <TrendingUp className={`h-4 w-4 ${
                          dashboardData.stats.userTrends.direction === 'up' ? 'text-green-500' : 'text-red-500'
                        }`} />
                        <span className={`text-sm ${
                          dashboardData.stats.userTrends.direction === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {dashboardData.stats.userTrends.percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <UserPlus className="h-8 w-8 text-purple-600" />
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600">Sesiones Activas</p>
                      <p className="text-2xl font-bold text-orange-900">{dashboardData.stats.totalSessions}</p>
                    </div>
                    <Activity className="h-8 w-8 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Registrations */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <UserPlus className="h-5 w-5 text-green-600" />
                    <span>Registros Recientes (7 días)</span>
                  </h3>
                  {dashboardData.recentRegistrations.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No hay registros recientes</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.recentRegistrations.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {user.displayName || user.email}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'admin' ? 'bg-red-100 text-red-800' :
                              user.role === 'shopkeeper' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {getRoleDisplayName(user.role)}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(user.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Login Activity */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <span>Actividad de Acceso</span>
                  </h3>
                  {dashboardData.loginActivity.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No hay actividad de acceso</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.loginActivity.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {user.displayName || user.email}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-700">
                              {formatDateTime(user.lastLogin)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {user.status === 'active' ? 'Activo' : 'Inactivo'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Role Changes */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-purple-600" />
                    <span>Cambios de Rol Recientes</span>
                  </h3>
                  {dashboardData.roleChanges.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No hay cambios de rol recientes</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.roleChanges.map((change) => (
                        <div key={change.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{change.userName}</p>
                            <p className="text-xs text-gray-500">
                              {getRoleDisplayName(change.oldRole)} → {getRoleDisplayName(change.newRole)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-700">
                              {formatDateTime(change.changedAt)}
                            </p>
                            <p className="text-xs text-gray-500">
                              por {change.changedBy}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Security Alerts */}
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <span>Alertas de Seguridad</span>
                  </h3>
                  <div className="space-y-4">
                    {/* Inactive Users */}
                    <div className="p-3 bg-yellow-50 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-yellow-800">Usuarios Inactivos (30+ días)</p>
                          <p className="text-xs text-yellow-600">
                            {dashboardData.securityAlerts.inactiveUsers.length} usuarios sin actividad reciente
                          </p>
                        </div>
                        <span className="text-lg font-bold text-yellow-900">
                          {dashboardData.securityAlerts.inactiveUsers.length}
                        </span>
                      </div>
                    </div>

                    {/* Multiple Logins */}
                    <div className="p-3 bg-orange-50 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-orange-800">Múltiples Sesiones</p>
                          <p className="text-xs text-orange-600">
                            Usuarios con múltiples sesiones activas
                          </p>
                        </div>
                        <span className="text-lg font-bold text-orange-900">
                          {dashboardData.securityAlerts.multipleLogins.length}
                        </span>
                      </div>
                    </div>

                    {/* Password Resets */}
                    <div className="p-3 bg-red-50 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-red-800">Restablecimientos de Contraseña</p>
                          <p className="text-xs text-red-600">
                            Solicitudes recientes de restablecimiento
                          </p>
                        </div>
                        <span className="text-lg font-bold text-red-900">
                          {dashboardData.securityAlerts.passwordResets.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserActivityDashboard;